// oxlint-disable-next-line wandb/no-deprecated-imports
import type {Message} from '../../providerMessage';
import type {RuntimeEvent} from '../events';
import type {
  ChunkDecoder,
  LLMToolSchema,
  PendingToolCall,
  ProviderAdapter,
  ProviderMessage,
  RuntimeConfig,
} from './adapter';

export class OpenAIResponsesAdapter implements ProviderAdapter {
  readonly targetHost = 'api.openai.com' as const;
  readonly targetPath = 'v1/responses';

  toProviderMessages(
    messages: Message[],
    systemPrompt: string
  ): ProviderMessage[] {
    const result: ProviderMessage[] = [];

    if (systemPrompt) {
      result.push({role: 'developer', content: systemPrompt});
    }

    for (const msg of messages) {
      if (msg.role === 'user') {
        result.push({role: 'user', content: getTextContent(msg)});
        continue;
      }

      if (msg.role === 'assistant') {
        const text = getTextContent(msg);
        if (text) {
          result.push({role: 'assistant', content: text});
        }

        // Emit tool calls and results as flat top-level items.
        for (const part of msg.content) {
          if (part.type === 'tool-call') {
            result.push({
              type: 'function_call',
              name: part.toolName,
              call_id: part.toolCallId,
              arguments: JSON.stringify(part.args ?? {}),
            });
          }
          if (part.type === 'tool-result') {
            result.push({
              type: 'function_call_output',
              call_id: part.toolCallId,
              output: part.content,
            });
          }
        }

        continue;
      }

      // System messages mapped to developer role in Responses API.
      if (msg.role === 'system') {
        const text = getTextContent(msg);
        if (text) {
          result.push({role: 'developer', content: text});
        }
      }
    }

    return result;
  }

  buildRequestBody(
    conversation: ProviderMessage[],
    tools: LLMToolSchema[],
    config: RuntimeConfig
  ): Record<string, unknown> {
    const body: Record<string, unknown> = {
      model: config.model,
      input: conversation,
      stream: true,
    };

    if (tools.length > 0) {
      body.tools = tools.map(t => ({
        type: 'function',
        name: t.name,
        description: t.description,
        parameters: t.parameters,
      }));
    }

    if (config.reasoningEffort) {
      body.reasoning = {effort: config.reasoningEffort};
    }

    return body;
  }

  createChunkDecoder(messageId: string): ChunkDecoder {
    let textStarted = false;
    let reasoningStarted = false;
    const outputIndexToToolCallIndex = new Map<number, number>();

    return (chunk: unknown, toolCalls: PendingToolCall[]): RuntimeEvent[] => {
      if (!isRecord(chunk)) {
        return [];
      }

      const type = chunk.type;
      if (typeof type !== 'string') {
        return [];
      }

      const events: RuntimeEvent[] = [];

      switch (type) {
        case 'response.output_text.delta': {
          if (!textStarted) {
            events.push({type: 'text-message-start', messageId});
            textStarted = true;
          }
          events.push({
            type: 'text-message-content',
            messageId,
            delta: stringField(chunk.delta),
          });
          break;
        }

        case 'response.output_text.done':
          events.push({type: 'text-message-end', messageId});
          break;

        case 'response.reasoning.delta': {
          if (!reasoningStarted) {
            events.push({type: 'reasoning-start', messageId});
            reasoningStarted = true;
          }
          events.push({
            type: 'reasoning-content',
            messageId,
            delta: stringField(chunk.delta),
          });
          break;
        }

        case 'response.reasoning.done':
          events.push({type: 'reasoning-end', messageId});
          break;

        case 'response.output_item.added': {
          const item = isRecord(chunk.item) ? chunk.item : null;
          if (item && item.type === 'function_call') {
            const callId = stringField(item.call_id);
            const name = stringField(item.name);
            outputIndexToToolCallIndex.set(
              numField(chunk.output_index, -1),
              toolCalls.length
            );
            toolCalls.push({id: callId, name, args: ''});
            events.push({
              type: 'tool-call-start',
              toolCallId: callId,
              toolName: name,
              messageId,
            });
          }
          break;
        }

        case 'response.function_call_arguments.delta': {
          const call = resolveToolCall(
            chunk,
            toolCalls,
            outputIndexToToolCallIndex
          );
          if (call) {
            const delta = stringField(chunk.delta);
            call.args += delta;
            events.push({type: 'tool-call-args', toolCallId: call.id, delta});
          }
          break;
        }

        case 'response.function_call_arguments.done': {
          const call = resolveToolCall(
            chunk,
            toolCalls,
            outputIndexToToolCallIndex
          );
          if (call) {
            call.args = stringField(chunk.arguments) || call.args;
            events.push({type: 'tool-call-end', toolCallId: call.id});
          }
          break;
        }

        // Known events that don't map to RuntimeEvents.
        case 'response.created':
        case 'response.in_progress':
        case 'response.completed':
        case 'response.output_item.done':
        case 'response.content_part.added':
        case 'response.content_part.done':
        case 'response.reasoning_summary_part.added':
        case 'response.reasoning_summary_part.done':
        case 'response.reasoning_summary_text.delta':
        case 'response.reasoning_summary_text.done':
          break;

        default:
          break;
      }

      return events;
    };
  }

  appendToolResult(
    conversation: ProviderMessage[],
    call: PendingToolCall,
    result: string
  ): void {
    conversation.push({
      type: 'function_call',
      name: call.name,
      call_id: call.id,
      arguments: call.args,
    });
    conversation.push({
      type: 'function_call_output',
      call_id: call.id,
      output: result,
    });
  }
}

/**
 * getTextContent extracts concatenated text from all text-type content parts of
 * a `Message`.
 */
function getTextContent(msg: Message): string {
  return msg.content.reduce(
    (acc, p) => (p.type === 'text' ? acc + p.text : acc),
    ''
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function numField(value: unknown, fallback: number): number {
  return typeof value === 'number' ? value : fallback;
}

function stringField(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function resolveToolCall(
  chunk: Record<string, unknown>,
  toolCalls: PendingToolCall[],
  indexMap: Map<number, number>
): PendingToolCall | undefined {
  const index = indexMap.get(numField(chunk.output_index, -1));
  return index !== undefined ? toolCalls[index] : undefined;
}
